// src/Pages/ReleaseEmails.js
import React, { useState, useEffect } from 'react';
import { api } from '../api';
import backArrowIcon from '../assets/icons/backarrow.svg';

export default function ReleaseEmails({ open, onClose, release, onReleaseUpdate }) {
  const [view, setView] = useState('list'); // 'list' or 'compose'
  const [selectedEmailType, setSelectedEmailType] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [cc, setCc] = useState('team@cldkid.com');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('08:00');

  // Available email types
  const emailTypes = [
    {
      id: 'releaseDetails',
      name: 'Release Details Email',
      description: 'Send release metadata and distribution information to artists',
      schedulable: false,
    },
    {
      id: 'outNow',
      name: 'Out Now Email',
      description: 'Send release announcement when the release goes live',
      schedulable: true,
    },
  ];

  // Reset view when modal opens/closes
  useEffect(() => {
    if (!open) {
      setView('list');
      setSelectedEmailType(null);
      setRecipient('');
      setCc('team@cldkid.com');
      setSubject('');
      setBody('');
      setStatus('');
    }
  }, [open]);

  // Fetch artist emails when release changes
  useEffect(() => {
    if (!open || !release || view !== 'compose') return;

    const fetchArtistEmails = async () => {
      try {
        // Get artist IDs from release.artists or release.mainArtists
        let artistIds = [];
        if (Array.isArray(release.artists)) {
          artistIds = release.artists.map(a => {
            if (typeof a === 'object' && a._id) return a._id;
            if (typeof a === 'string') return a;
            return null;
          }).filter(Boolean);
        } else if (Array.isArray(release.mainArtists)) {
          artistIds = release.mainArtists.map(a => {
            if (typeof a === 'object' && a._id) return a._id;
            return null;
          }).filter(Boolean);
        }
        
        if (artistIds.length === 0) {
          setRecipient('');
          return;
        }

        // Fetch all artists to get their emails
        const res = await api('/api/artists');
        if (!res.ok) throw new Error('Failed to fetch artists');
        const allArtists = await res.json();
        
        // Collect all emails from artists linked to this release
        const emails = [];
        for (const artist of allArtists) {
          if (artistIds.some(id => String(id) === String(artist._id))) {
            if (Array.isArray(artist.emails)) {
              emails.push(...artist.emails.filter(Boolean));
            }
          }
        }
        
        setRecipient(emails.join(', '));
      } catch (err) {
        console.error('Failed to fetch artist emails:', err);
        setRecipient('');
      }
    };

    fetchArtistEmails();
  }, [open, release, view]);

  // Auto-fill email fields when email type or release changes
  useEffect(() => {
    if (!open || !release || view !== 'compose') return;

    // Get release title
    const releaseTitle = release.title || 'Release';

    // Get catalogue number - check if single release
    const isSingle = (release.releaseType || '').toLowerCase().includes('single');
    const catalogueNumber = isSingle 
      ? (release.trackCatalogNumber || 'Not set') 
      : (release.assignedProjectCatalogNumber || 'Not set');

    // Get all artist names
    let artistNames = [];
    if (Array.isArray(release.artists) && release.artists.length > 0) {
      artistNames = release.artists
        .map(a => typeof a === 'object' && a.name ? a.name : null)
        .filter(Boolean);
    } else if (Array.isArray(release.mainArtists) && release.mainArtists.length > 0) {
      artistNames = release.mainArtists
        .map(a => typeof a === 'object' && a.name ? a.name : null)
        .filter(Boolean);
    }
    
    // Format artist name(s) for email greeting
    let artistName = 'Artist';
    if (artistNames.length === 1) {
      artistName = artistNames[0];
    } else if (artistNames.length > 1) {
      // Join with commas and "and" for the last one
      if (artistNames.length === 2) {
        artistName = `${artistNames[0]} and ${artistNames[1]}`;
      } else {
        artistName = `${artistNames.slice(0, -1).join(', ')}, and ${artistNames[artistNames.length - 1]}`;
      }
    }

    // Format release date
    const formattedDate = release.releaseDate
      ? new Date(release.releaseDate)
          .toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          .replaceAll('/', '.')
      : 'Not set';

    // Get UPC - check if single release
    const upc = isSingle ? (release.trackUpc || 'Not set') : (release.assignedProjectUpc || 'Not set');

    if (selectedEmailType === 'releaseDetails') {
      // Set subject
      setSubject(`(${catalogueNumber}) ${releaseTitle} - Release Details`);

      // Build email body (plain text version with markdown-style bold)
      const emailBody = `Hey ${artistName},

"${releaseTitle}" has been distributed and is ready for release! Find all useful information below:

**Release Date:** ${formattedDate}
**Presave/Fanlink:** ${release.presaveLink || 'Not set'}
**ISRC:** ${release.isrc || 'Not set'}
**UPC:** ${upc}

Feel free to pitch your release on Spotify for Artists yourself, otherwise we'll do this for you.

- Team CloudKid`;

      setBody(emailBody);
    } else if (selectedEmailType === 'outNow') {
      // Set subject
      setSubject(`(${catalogueNumber}) ${releaseTitle} - Out Now`);

      // Build email body
      const emailBody = `Hey ${artistName},

"${releaseTitle}" is now live - congratulations on a fantastic release!

It has also been added to our CloudKid playlists to give it an extra boost and help reach more listeners.

We'll keep you updated on any additional promotional opportunities as they come up.

Useful information:

**Release Date:** ${formattedDate}
**Presave/Fanlink:** ${release.presaveLink || 'Not set'}
**ISRC:** ${release.isrc || 'Not set'}
**UPC:** ${upc}

If you have any questions or need support, don't hesitate to reach out.

- Team CloudKid`;

      setBody(emailBody);

      // Set default scheduled date/time based on release date or existing schedule
      const scheduledEmails = release?.scheduledEmails || [];
      const existingScheduled = scheduledEmails.find(e => e.emailType === selectedEmailType);
      
      if (existingScheduled) {
        // Pre-fill with existing scheduled date/time
        // The scheduledDateTime is stored in UTC, convert back to German time for display
        const existingDate = new Date(existingScheduled.scheduledDateTime);
        
        // Use toLocaleString with German timezone to get accurate conversion
        const germanDateStr = existingDate.toLocaleString('en-CA', {
          timeZone: 'Europe/Berlin',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        
        // Format: "YYYY-MM-DD, HH:MM"
        const [datePart, timePart] = germanDateStr.split(', ');
        const [year, month, day] = datePart.split('-');
        const [hours, minutes] = timePart.split(':');
        
        setScheduledDate(`${year}-${month}-${day}`);
        setScheduledTime(`${hours}:${minutes}`);
      } else if (release.releaseDate) {
        // Set default based on release date
        const releaseDate = new Date(release.releaseDate);
        const year = releaseDate.getFullYear();
        const month = String(releaseDate.getMonth() + 1).padStart(2, '0');
        const day = String(releaseDate.getDate()).padStart(2, '0');
        setScheduledDate(`${year}-${month}-${day}`);
        setScheduledTime('08:00');
      }
    }
  }, [open, release, view, selectedEmailType]);

  const handleSend = async () => {
    if (!recipient.trim()) {
      setStatus('⚠️ Please enter at least one recipient');
      return;
    }
    if (!subject.trim()) {
      setStatus('⚠️ Please enter a subject');
      return;
    }
    if (!body.trim()) {
      setStatus('⚠️ Please enter email body');
      return;
    }

    setSending(true);
    setStatus('');

    try {
      // Convert plain text body to HTML with bold labels
      const htmlBody = body
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert markdown bold to HTML
        .replace(/\n/g, '<br>'); // Convert line breaks to HTML

      const res = await api('/api/send-release-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient: recipient.trim(),
          cc: cc.trim() || undefined,
          subject: subject.trim(),
          text: body.trim(),
          html: htmlBody,
          releaseId: release?._id,
          emailType: selectedEmailType,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to send email');
      }

      // Update release to mark email as sent
      if (release?._id && selectedEmailType) {
        const sentEmails = Array.isArray(release.sentEmails) ? release.sentEmails : [];
        if (!sentEmails.includes(selectedEmailType)) {
          release.sentEmails = [...sentEmails, selectedEmailType];
        }
        // Notify parent to refresh release data
        if (onReleaseUpdate) {
          onReleaseUpdate();
        }
      }

      setStatus('✅ Email sent successfully!');
      setTimeout(() => {
        // Go back to list view instead of closing
        setView('list');
        setSelectedEmailType(null);
        setStatus('');
      }, 1500);
    } catch (err) {
      setStatus(`❌ ${err.message || 'Failed to send email'}`);
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!recipient.trim()) {
      setStatus('⚠️ Please enter at least one recipient');
      return;
    }
    if (!subject.trim()) {
      setStatus('⚠️ Please enter a subject');
      return;
    }
    if (!body.trim()) {
      setStatus('⚠️ Please enter email body');
      return;
    }
    if (!scheduledDate) {
      setStatus('⚠️ Please select a schedule date');
      return;
    }
    if (!scheduledTime) {
      setStatus('⚠️ Please select a schedule time');
      return;
    }

    // Check if email is already scheduled
    const scheduledEmails = release?.scheduledEmails || [];
    const existingScheduled = scheduledEmails.find(e => e.emailType === selectedEmailType);
    
    if (existingScheduled) {
      // The scheduledDateTime is stored in UTC, convert back to German time
      const existingDate = new Date(existingScheduled.scheduledDateTime);
      const formattedExistingDate = existingDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Berlin',
      }).replaceAll('/', '.');
      const formattedExistingTime = existingDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Berlin',
      });
      
      const confirmMessage = `This email is already scheduled for ${formattedExistingDate} at ${formattedExistingTime} (German Time).\n\nDo you want to overwrite it with the new schedule?`;
      
      if (!window.confirm(confirmMessage)) {
        return; // User cancelled
      }
    }

    setSending(true);
    setStatus('');

    try {
      // Convert plain text body to HTML with bold labels
      const htmlBody = body
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert markdown bold to HTML
        .replace(/\n/g, '<br>'); // Convert line breaks to HTML

      // Combine date and time, convert to German time (CET/CEST)
      const scheduledDateTime = `${scheduledDate}T${scheduledTime}:00`;

      const res = await api('/api/schedule-release-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient: recipient.trim(),
          cc: cc.trim() || undefined,
          subject: subject.trim(),
          text: body.trim(),
          html: htmlBody,
          releaseId: release?._id,
          emailType: selectedEmailType,
          scheduledDateTime,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to schedule email');
      }

      // Notify parent to refresh release data
      if (onReleaseUpdate) {
        onReleaseUpdate();
      }

      setStatus('✅ Email scheduled successfully!');
      setTimeout(() => {
        // Go back to list view instead of closing
        setView('list');
        setSelectedEmailType(null);
        setStatus('');
      }, 1500);
    } catch (err) {
      setStatus(`❌ ${err.message || 'Failed to schedule email'}`);
    } finally {
      setSending(false);
    }
  };

  const isSchedulable = emailTypes.find(e => e.id === selectedEmailType)?.schedulable || false;

  if (!open) return null;

  // List view - show all email types
  if (view === 'list') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 600,
            maxWidth: '95vw',
            background: '#1e1e1e',
            borderRadius: 12,
            padding: 20,
            color: '#fff',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700 }}>
            Send Email
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {emailTypes.map((emailType) => {
              // Check if this email type has been sent or scheduled
              const sentEmails = release?.sentEmails || [];
              const scheduledEmails = release?.scheduledEmails || [];
              const isSent = Array.isArray(sentEmails) && sentEmails.includes(emailType.id);
              const isScheduled = Array.isArray(scheduledEmails) && scheduledEmails.some(e => e.emailType === emailType.id);
              
              return (
                <div
                  key={emailType.id}
                  onClick={() => {
                    setSelectedEmailType(emailType.id);
                    setView('compose');
                  }}
                  style={{
                    padding: 16,
                    background: '#2a2a2a',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#2a2a2a';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      {emailType.name}
                    </div>
                    {isSent ? (
                      <div
                        style={{
                          padding: '4px 8px',
                          background: '#1e3a1e',
                          color: '#4caf50',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginLeft: 12,
                        }}
                      >
                        ✓ Sent
                      </div>
                    ) : isScheduled ? (
                      <div
                        style={{
                          padding: '4px 8px',
                          background: '#1e2a3a',
                          color: '#5dade2',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginLeft: 12,
                        }}
                      >
                        Scheduled
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: '4px 8px',
                          background: '#2a2a2a',
                          color: '#999',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginLeft: 12,
                        }}
                      >
                        Not sent
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>
                    {emailType.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Compose view - show email composition form
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 600,
          maxWidth: '95vw',
          background: '#1e1e1e',
          borderRadius: 12,
          padding: 20,
          color: '#fff',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <button
            onClick={() => {
              setView('list');
              setSelectedEmailType(null);
              setStatus('');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              marginRight: 12,
              display: 'flex',
              alignItems: 'center',
            }}
            title="Back to email types"
          >
            <img
              src={backArrowIcon}
              alt="Back"
              style={{
                width: 16,
                height: 16,
                filter: 'brightness(0) saturate(100%) invert(20%)',
                opacity: 0.8,
              }}
            />
          </button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            {emailTypes.find(e => e.id === selectedEmailType)?.name || 'Send Email'}
          </h2>
        </div>

        {/* Already Scheduled Warning */}
        {emailTypes.find(e => e.id === selectedEmailType)?.schedulable && (() => {
          const scheduledEmails = release?.scheduledEmails || [];
          const sentEmails = release?.sentEmails || [];
          const existingScheduled = scheduledEmails.find(e => e.emailType === selectedEmailType);
          const isSent = Array.isArray(sentEmails) && sentEmails.includes(selectedEmailType);
          
          if (existingScheduled) {
            // The scheduledDateTime is stored in UTC, convert back to German time
            const existingDate = new Date(existingScheduled.scheduledDateTime);
            // Use toLocaleString with German timezone to get accurate conversion
            const formattedDate = existingDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              timeZone: 'Europe/Berlin',
            }).replaceAll('/', '.');
            const formattedTime = existingDate.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'Europe/Berlin',
            });
            
            return (
              <div
                style={{
                  marginBottom: 20,
                  padding: 12,
                  background: '#1e2a3a',
                  borderRadius: 6,
                  border: '1px solid #5dade2',
                }}
              >
                <div style={{ fontSize: 13, color: '#5dade2', fontWeight: 600, marginBottom: 4 }}>
                  ⏰ Already Scheduled
                </div>
                <div style={{ fontSize: 12, color: '#aaa' }}>
                  Currently scheduled for {formattedDate} at {formattedTime} (German Time)
                </div>
              </div>
            );
          } else if (isSent) {
            // Email was sent from a scheduled email, show that it was sent
            return (
              <div
                style={{
                  marginBottom: 20,
                  padding: 12,
                  background: '#1e3a1e',
                  borderRadius: 6,
                  border: '1px solid #4caf50',
                }}
              >
                <div style={{ fontSize: 13, color: '#4caf50', fontWeight: 600, marginBottom: 4 }}>
                  ✓ Already Sent
                </div>
                <div style={{ fontSize: 12, color: '#aaa' }}>
                  This email has already been sent. You can reschedule it with a new date and time.
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Already Sent Warning (for non-schedulable emails) */}
        {!emailTypes.find(e => e.id === selectedEmailType)?.schedulable && (() => {
          const sentEmails = release?.sentEmails || [];
          const isSent = Array.isArray(sentEmails) && sentEmails.includes(selectedEmailType);
          
          if (isSent) {
            return (
              <div
                style={{
                  marginBottom: 20,
                  padding: 12,
                  background: '#1e3a1e',
                  borderRadius: 6,
                  border: '1px solid #4caf50',
                }}
              >
                <div style={{ fontSize: 13, color: '#4caf50', fontWeight: 600, marginBottom: 4 }}>
                  ✓ Already Sent
                </div>
                <div style={{ fontSize: 12, color: '#aaa' }}>
                  This email has already been sent. You can resend it with updated information.
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Recipient */}
        <label style={{ marginBottom: 8, fontWeight: 600 }}>To:</label>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="artist@example.com"
          style={{
            width: '100%',
            padding: 10,
            background: '#2a2a2a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            marginBottom: 15,
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* CC */}
        <label style={{ marginBottom: 8, fontWeight: 600 }}>CC (optional):</label>
        <input
          type="text"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          placeholder="cc@example.com"
          style={{
            width: '100%',
            padding: 10,
            background: '#2a2a2a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            marginBottom: 15,
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* Subject */}
        <label style={{ marginBottom: 8, fontWeight: 600 }}>Subject:</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={{
            width: '100%',
            padding: 10,
            background: '#2a2a2a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            marginBottom: 15,
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* Body */}
        <label style={{ marginBottom: 8, fontWeight: 600 }}>Body:</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          style={{
            width: '100%',
            padding: 10,
            background: '#2a2a2a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            marginBottom: 15,
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />

        {/* Scheduling fields for schedulable emails */}
        {emailTypes.find(e => e.id === selectedEmailType)?.schedulable && (
          <>
            <label style={{ marginBottom: 8, fontWeight: 600 }}>Schedule Date & Time (German Time):</label>
            <div style={{ display: 'flex', gap: 12, marginBottom: 15 }}>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                style={{
                  flex: 1,
                  padding: 10,
                  background: '#2a2a2a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                style={{
                  flex: 1,
                  padding: 10,
                  background: '#2a2a2a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </>
        )}

        {/* Status message */}
        {status && (
          <div
            style={{
              marginBottom: 15,
              padding: 10,
              borderRadius: 6,
              background: status.startsWith('✅') ? '#1e3a1e' : status.startsWith('⚠️') ? '#3a2e1e' : '#3a1e1e',
              color: status.startsWith('✅') ? '#4caf50' : status.startsWith('⚠️') ? '#f39c12' : '#f66',
              fontWeight: 600,
            }}
          >
            {status}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding: '10px 20px',
              background: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: sending ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          {isSchedulable ? (() => {
            const scheduledEmails = release?.scheduledEmails || [];
            const sentEmails = release?.sentEmails || [];
            const existingScheduled = scheduledEmails.find(e => e.emailType === selectedEmailType);
            const isSent = Array.isArray(sentEmails) && sentEmails.includes(selectedEmailType);
            const isRescheduling = !!existingScheduled || isSent;
            
            return (
              <button
                onClick={handleSchedule}
                disabled={sending || !recipient.trim() || !subject.trim() || !body.trim() || !scheduledDate || !scheduledTime}
                style={{
                  padding: '10px 20px',
                  background: sending || !recipient.trim() || !subject.trim() || !body.trim() || !scheduledDate || !scheduledTime ? '#555' : (isRescheduling ? '#666' : '#1DB954'),
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: sending || !recipient.trim() || !subject.trim() || !body.trim() || !scheduledDate || !scheduledTime ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {sending ? (isRescheduling ? 'Rescheduling...' : 'Scheduling...') : (isRescheduling ? 'Reschedule Email' : 'Schedule Email')}
              </button>
            );
          })() : (() => {
            const sentEmails = release?.sentEmails || [];
            const isResending = Array.isArray(sentEmails) && sentEmails.includes(selectedEmailType);
            
            return (
              <button
                onClick={handleSend}
                disabled={sending || !recipient.trim() || !subject.trim() || !body.trim()}
                style={{
                  padding: '10px 20px',
                  background: sending || !recipient.trim() || !subject.trim() || !body.trim() ? '#555' : (isResending ? '#666' : '#1DB954'),
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: sending || !recipient.trim() || !subject.trim() || !body.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {sending ? (isResending ? 'Resending...' : 'Sending...') : (isResending ? 'Resend Email' : 'Send Email')}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

